import type { RowData } from "@tanstack/react-table";
import { toDisplayText } from "../cells/cellValues";
import {
    isPeopleValue,
    asDateOnlyRange,
    toInstant,
} from "../cells/listDisplayValues";
import type { DataGridRow } from "./types";

const textCollator = new Intl.Collator(undefined, {
    sensitivity: "base",
    numeric: true,
});

const toSortText = <TValue>(value: TValue): string | null => {
    if (typeof value === "string") {
        return value === "" ? null : value;
    }
    return typeof value === "number" ? String(value) : null;
};

/** Plain strings pass straight through; other values read as their cell text. */
const toTextKey = <TValue>(value: TValue): string | null =>
    typeof value === "string" ? toSortText(value) : toDisplayText(value);

const toSortNumber = <TValue>(value: TValue): number | null =>
    typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * TanStack negates a sort function's result for descending columns. Empties
 * must stay last either way, so their result is pre-flipped.
 */
const isSortReversed = <TData extends RowData>(
    row: DataGridRow<TData>,
    columnId: string,
): boolean =>
    row.table.atoms.sorting
        .get()
        .some((sort) => sort.id === columnId && sort.desc);

interface SortKeyCache<TKey> {
    rows: object[];
    keys: Array<TKey | null>;
}

/**
 * Each row's key is computed once per sort run instead of once per
 * comparison. Slots are addressed by `row.index` and checked by row identity,
 * since sub-row indexes repeat across parents. The cache lives for the
 * synchronous sort and is dropped in the next microtask.
 */
const createSort = <TKey>(
    toKey: <TRaw>(value: TRaw) => TKey | null,
    compare: (left: TKey, right: TKey) => number,
) => {
    let cachesByColumn: Map<string, SortKeyCache<TKey>> | null = null;

    const getCache = (columnId: string) => {
        if (cachesByColumn === null) {
            cachesByColumn = new Map();
            queueMicrotask(() => {
                cachesByColumn = null;
            });
        }
        let cache = cachesByColumn.get(columnId);
        if (cache === undefined) {
            cache = { rows: [], keys: [] };
            cachesByColumn.set(columnId, cache);
        }
        return cache;
    };

    const readKey = <TData extends RowData>(
        cache: SortKeyCache<TKey>,
        row: DataGridRow<TData>,
        columnId: string,
    ) => {
        if (cache.rows[row.index] === row) {
            return cache.keys[row.index];
        }
        const key = toKey(row.getValue(columnId));
        cache.rows[row.index] = row;
        cache.keys[row.index] = key;
        return key;
    };

    return <TData extends RowData>(
        rowA: DataGridRow<TData>,
        rowB: DataGridRow<TData>,
        columnId: string,
    ): number => {
        const cache = getCache(columnId);
        const a = readKey(cache, rowA, columnId);
        const b = readKey(cache, rowB, columnId);
        if (a === null || b === null) {
            if (a === b) {
                return 0;
            }
            const emptyLast = a === null ? 1 : -1;
            return isSortReversed(rowA, columnId) ? -emptyLast : emptyLast;
        }
        return compare(a, b);
    };
};

const compareNumbers = (left: number, right: number) => left - right;
const compareStrings = (left: string, right: string) => {
    if (left === right) {
        return 0;
    }
    return left > right ? 1 : -1;
};

interface TextSortKey {
    text: string;
    /** Lowercased when plain `compareStrings` order matches the collator. */
    folded: string | null;
}

const ASCII_WORDS = /^[a-z ]*$/i;
const canFoldAscii = textCollator.resolvedOptions().locale.startsWith("en");

const toTextSortKey = <TValue>(value: TValue): TextSortKey | null => {
    const text = toTextKey(value);
    if (text === null) {
        return null;
    }
    const isFoldable = canFoldAscii && ASCII_WORDS.test(text);
    return { text, folded: isFoldable ? text.toLowerCase() : null };
};

const compareText = (left: TextSortKey, right: TextSortKey) => {
    if (left.folded !== null && right.folded !== null) {
        return compareStrings(left.folded, right.folded);
    }
    return left.text === right.text
        ? 0
        : textCollator.compare(left.text, right.text);
};

/**
 * Locale-aware, case-insensitive text order with numbers in numeric order;
 * objects (entities, accounts, counterparties) sort by their name. In every
 * sort, empty values sort last in both directions.
 */
export const sortText = createSort(toTextSortKey, compareText);

/** Numeric order for number and money columns. */
export const sortNumber = createSort(toSortNumber, compareNumbers);

/** `YYYY-MM-DD` strings order lexically, which is calendar order. */
export const sortDateOnly = createSort(toSortText, compareStrings);

const toInstantTime = <TValue>(value: TValue): number | null =>
    toInstant(value)?.getTime() ?? null;

/** Instants in time order, whatever zone offset their ISO text carries. */
export const sortTimestamp = createSort(toInstantTime, compareNumbers);

interface DateRangeSortKey {
    from: string | null;
    to: string | null;
}

const toDateRangeSortKey = <TValue>(value: TValue): DateRangeSortKey | null => {
    const range = asDateOnlyRange(value);
    if (
        range === null ||
        (range.from === undefined && range.to === undefined)
    ) {
        return null;
    }
    return { from: range.from ?? null, to: range.to ?? null };
};

/** Days with a missing side after those with one. */
const compareOpenDays = (left: string | null, right: string | null) => {
    if (left === null || right === null) {
        return Number(left === null) - Number(right === null);
    }
    return compareStrings(left, right);
};

/** `DateOnlyRange`s by `from`, then `to`. */
export const sortDateRange = createSort(
    toDateRangeSortKey,
    (left, right) =>
        compareOpenDays(left.from, right.from) ||
        compareOpenDays(left.to, right.to),
);

const toFirstMemberSortKey = <TValue>(value: TValue) =>
    isPeopleValue(value) ? toTextSortKey(value[0]) : null;

/** People columns by their first member's name. */
export const sortPeople = createSort(toFirstMemberSortKey, compareText);

/** Orders status values by their position in `orderedValues`; unknown ones next. */
export const createStatusSort = (orderedValues: readonly string[]) => {
    const rankByValue = new Map(
        orderedValues.map((value, index) => [value, index]),
    );
    const toRank = <TRaw>(value: TRaw) => {
        const text = toSortText(value);
        return text === null
            ? null
            : (rankByValue.get(text) ?? orderedValues.length);
    };
    return createSort(toRank, compareNumbers);
};
